import type { IComponentPreference, PreferenceType } from '~/modules/preferences/types';

import { useCallback, useEffect, useRef } from 'react';
import { isBrowser } from '~/utils';

const POLARIS_PAGE_PADDING_BOTTOM = 108;
const GAP_BETWEEN_TARGETS = 32;
const TARGET_THRESHOLD = 0.5;
const OBSERVER_THRESHOLD = Array.from({ length: 11 }, (_v, i) => i / 10);

type PreferencesTargetList = Record<string, { show: boolean }>;

interface UseIntersectionPreferencesMenuProps {
  isLayoutStable: boolean;
  componentPreferences: IComponentPreference[];
  onSelect: (targetId: PreferenceType) => void;
}

export const useIntersectionPreferencesMenu = ({
  isLayoutStable,
  componentPreferences,
  onSelect,
}: UseIntersectionPreferencesMenuProps) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const obsHeight = useRef<number>(0);

  const preferencesTargetList = useRef<PreferencesTargetList>({});
  const handleSelectRef = useRef(onSelect);

  const initIntersectionObserver = useCallback(() => {
    const handleThresholdReach = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const entryTargetId = entry.target.id;

        const target = preferencesTargetList.current[entryTargetId];
        if (!target) return;

        preferencesTargetList.current[entryTargetId].show =
          entry.intersectionRatio > TARGET_THRESHOLD;
      });

      for (const target of Object.keys(preferencesTargetList.current)) {
        if (preferencesTargetList.current[target].show) {
          handleSelectRef.current(target as PreferenceType);
          break;
        }
      }
    };

    observer.current = new IntersectionObserver(handleThresholdReach, {
      root: null,
      rootMargin: '0px',
      threshold: OBSERVER_THRESHOLD,
    });

    for (const target of Object.keys(preferencesTargetList.current)) {
      const observeDom = document.getElementById(target);
      if (!observeDom) continue;
      observer.current?.observe(observeDom);
    }
  }, []);

  const unsubscribeIntersectionObserver = useCallback(() => {
    const obs = observer.current;
    if (!obs) return;

    for (const target of Object.keys(preferencesTargetList.current)) {
      const observeDom = document.getElementById(target);
      if (!observeDom) continue;
      obs.unobserve(observeDom);
    }

    obs.disconnect();
    observer.current = null;
  }, []);

  useEffect(() => {
    preferencesTargetList.current = componentPreferences.reduce((acc, curr) => {
      acc[curr.type] = { show: false };
      return acc;
    }, {} as PreferencesTargetList);
  }, [componentPreferences]);

  useEffect(() => {
    handleSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!isBrowser()) return;

    initIntersectionObserver();

    let allTargetHeight = 0;
    let subTargetHeight = 0;

    for (const target of Object.keys(preferencesTargetList.current)) {
      const observeDom = document.getElementById(target);
      if (!observeDom) continue;

      const isLastTarget = target === Object.keys(preferencesTargetList.current).at(-1);
      if (isLastTarget) subTargetHeight = observeDom.clientHeight;
      allTargetHeight += observeDom.clientHeight + (isLastTarget ? 0 : GAP_BETWEEN_TARGETS);
    }

    const viewport = document.querySelector('html')?.clientHeight ?? 0;
    obsHeight.current =
      allTargetHeight + viewport - (subTargetHeight + POLARIS_PAGE_PADDING_BOTTOM);

    return () => unsubscribeIntersectionObserver();
    // Keep `isLayoutStable` in the dependency array to re-run this effect until the layout is stable
  }, [isLayoutStable, initIntersectionObserver, unsubscribeIntersectionObserver]);

  return {
    obsHeight: obsHeight.current,
  };
};
